import { type ReactElement, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ExportDrawer } from '@features/exports/elements/ExportDrawer'
import { ExportFileTypeSection } from '@features/exports/elements/ExportFileTypeSection'
import { type ExportDocumentFileType } from '@features/exports/types/exportDocumentFileType'
import { useExportProducts } from '@features/products/hooks/useExportProducts'

import { defaultProductsExportFileType, productsExportFileTypes } from './constants/productsExportFileTypes'

interface ProductsExportDrawerProps {
  isOpen: boolean
  onClose: () => void
}

export const ProductsExportDrawer = ({ isOpen, onClose }: ProductsExportDrawerProps): ReactElement => {
  const [fileType, setFileType] = useState<ExportDocumentFileType | undefined>(defaultProductsExportFileType)
  const { t } = useTranslation()
  const { exportProducts, isLoading } = useExportProducts({ enabled: isOpen })

  const handleDrawerClose = useCallback(() => {
    setFileType(defaultProductsExportFileType)
    onClose()
  }, [onClose])

  const handleDrawerExport = useCallback(() => {
    if (!fileType || isLoading) {
      return
    }

    exportProducts(fileType)
    handleDrawerClose()
  }, [exportProducts, fileType, handleDrawerClose, isLoading])

  return (
    <ExportDrawer
      isExportDisabled={!fileType || isLoading}
      isExporting={isLoading}
      isOpen={isOpen}
      onClose={handleDrawerClose}
      onExport={handleDrawerExport}
      title={t('products.export_drawer.title')}
    >
      <ExportFileTypeSection fileTypes={productsExportFileTypes} onChange={setFileType} value={fileType} />
    </ExportDrawer>
  )
}
